// Pure grouping helper for the Recent Activity feed.
//
// The server (get_recent_activity in netlify/functions/db.js) returns ONE row
// per player for solo stroke-play rounds. When two or more friends played the
// SAME physical round (same standalone_round_id), that surfaces as duplicate
// "finished a round" tiles — one per person. This helper collapses those into
// a single co-played group so the feed can render one merged tile listing each
// participant's final total_score.
//
// Only SOLO STROKE-PLAY rows are merged. Team/match/scramble rounds are already
// collapsed to one representative row by the server (see the "Friends' team
// rounds: only one representative row per team" branch in db.js), so re-merging
// them here would double-handle them — they pass through as singletons. Event
// rounds (standalone_round_id NULL) never group: a NULL key would otherwise
// fold every event round into one bogus tile.
//
// "One score written once" — this reads the total_score already present on each
// row; it never recomputes or double-counts a score.
//
// VISIBILITY — a co-played merge only appears when the other participant is an
// ACCEPTED friend. get_recent_activity (db.js) restricts the rows it returns to
// the caller's friend_set (self + status='accepted' friendships), by design: the
// feed never surfaces a non-friend's name or score. So a casual/stub playing
// partner added only by phone (who never sent/accepted a friend request) does
// NOT produce a second row here, and the round stays a single-player tile — this
// is expected, not a bug. Merging never widens visibility beyond what the server
// already handed the client.

function isMergeableSoloStroke(r) {
  const rt = (r.round_type || 'stroke').toLowerCase()
  const isTeam = rt === 'match' || rt === '2v2'
  const isScramble = r.is_scramble === true
  return !isTeam && !isScramble && r.team_number == null && r.standalone_round_id != null
}

// groupActivityByRound(rows, viewerId)
//   rows     — the get_recent_activity array, already sorted played_at DESC.
//   viewerId — the logged-in player's id (players.id), used to mark "You".
//
// Returns a new array in the SAME order. Each element is either:
//   • an original row (non-mergeable rows, and solo rounds with 1 participant),
//     rendered by the existing single-player / "competed in" card paths; or
//   • a merged co-played object: the first-appearing member row spread onto a
//     `{ co_played: true, participants: [...] }` shape. `participants` lists the
//     viewer first (when present), then the other members ALPHABETICALLY by
//     player_name (owner-chosen deterministic order — the same round renders
//     identically on every load and device). Groups without the viewer are
//     fully alphabetical.
//
// Group order is anchored at each group's earliest-appearing member, so the
// server's played_at DESC ordering is preserved and singletons never reshuffle.
export function groupActivityByRound(rows, viewerId) {
  if (!Array.isArray(rows)) return []

  const out = []
  const indexByKey = new Map()

  for (const r of rows) {
    if (!isMergeableSoloStroke(r)) {
      out.push(r)
      continue
    }
    const key = r.standalone_round_id
    if (indexByKey.has(key)) {
      out[indexByKey.get(key)].__members.push(r)
    } else {
      indexByKey.set(key, out.length)
      out.push({ __group: true, __members: [r] })
    }
  }

  return out.map((item) => {
    if (!item || !item.__group) return item
    const members = item.__members
    // A single-participant solo round is not "co-played" — leave the original
    // row untouched so the existing single-player tile renders unchanged.
    if (members.length === 1) return members[0]

    // Viewer first (when they're in the round), then alphabetical by
    // player_name (the raw name, NOT the rendered "You" label). Fixed 'en'
    // locale + case-insensitive comparison keeps the order deterministic
    // across devices/locales; nameless rows (anonymous stubs) sort last.
    // Array sort is stable (ES2019+), so exact ties keep server order.
    const byName = (a, b) => {
      const an = a.player_name
      const bn = b.player_name
      if (an == null && bn == null) return 0
      if (an == null) return 1
      if (bn == null) return -1
      return String(an).localeCompare(String(bn), 'en', { sensitivity: 'base' })
    }
    const ordered = [...members].sort((a, b) => {
      const aMe = a.player_id === viewerId ? 0 : 1
      const bMe = b.player_id === viewerId ? 0 : 1
      if (aMe !== bMe) return aMe - bMe
      return byName(a, b)
    })

    const base = members[0]
    return {
      ...base,
      co_played: true,
      participants: ordered.map((r) => ({
        player_id: r.player_id,
        player_name: r.player_name,
        total_score: r.total_score,
        score_vs_par: r.score_vs_par,
        is_me: r.player_id === viewerId,
      })),
    }
  })
}
